import type { IsoDate, IsoDateTime, IsoDateTimeTz, IsoDuration } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';

/**
 * Add a number of years to an ISO date/date-time string while preserving its shape.
 *
 * Supported inputs:
 * - Date-only: YYYY-MM-DD -> returns YYYY-MM-DD
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])? -> returns same shape
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM) -> returns same shape, preserving the original tz literal
 * - Duration: PyYmMdD -> returns YYYY-MM-DD
 *
 * Rules:
 * - Only the calendar date portion is changed; time-of-day and fractional seconds are preserved verbatim.
 * - For leap-day inputs (e.g., 2024-02-29), if the resulting year has no Feb 29, the day is clamped to Feb 28.
 * - `amount` must be a finite integer (can be negative), or an ISO 8601 duration string with years only (e.g., P3Y, -P2Y).
 */
export function addYears<T extends IsoDate | IsoDateTime | IsoDateTimeTz | IsoDuration>(input: T, amount: number): T {
  if(!Number.isInteger(amount) || !isFinite(amount))
    throw new Error('addYears: amount must be a finite integer');
  if(amount === 0)
    return input;

  const deconstructed = deconstructDate(input);
  if(deconstructed._type !== 'date'
    && deconstructed._type !== 'dateTime'
    && deconstructed._type !== 'dateTimeTz'
    && deconstructed._type !== 'duration') {
    throw new Error('addDays: invalid ISO date/time/duration string');
  }
  if(deconstructed._type === 'duration') {
    deconstructed.years = (deconstructed.years ?? 0) + amount;
    if(deconstructed.years < 0)
      throw new Error('addMonths: resulting duration would be negative');
    return constructDate(deconstructed) as unknown as T;
  }

  const {year, month, day} = deconstructed;
  const targetYear = year + amount;

  const lastDay = new Date(Date.UTC(targetYear, month, 0)).getUTCDate();
  const targetDay = Math.min(day, lastDay);

  const dt = new Date(Date.UTC(targetYear, month -1, targetDay));
  deconstructed.year = dt.getUTCFullYear();
  deconstructed.month = dt.getUTCMonth() + 1;
  deconstructed.day = dt.getUTCDate();
  return constructDate(deconstructed) as unknown as T;
}
